import { useEffect, useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Sheet as BottomSheet } from '../../components/Overlay.tsx';
import { Seg } from '../../components/Seg.tsx';
import { StatusPill, PriorityPill } from '../../components/Pill.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { patchEntry, shareableNotes } from '../../api/rooms.ts';
import type { Entry, EntryMode, ItemKind, PrayerNote, Sheet } from '../../domain.ts';

/**
 * F4-2 기도 제목 적기 · F4-2-1 내 Prayer Book 으로 저장.
 *
 * 줄 머리의 연필을 누르면 올라오는 시트입니다. **기도 제목은 보통 순장이
 * 받아 적으므로** 그룹장은 모든 구성원의 줄을, 그룹원은 자기 줄을 적습니다.
 *
 * 적는 길이 둘입니다.
 *
 *   직접 적기        제목(선택) · 내용(필수)
 *   대표 기도제목 공유  그 사람의 대표 중에서 **여러 개를 함께** 고릅니다
 *
 * 닫는 길은 셋 — 저장 · 손잡이를 아래로 끌기 · 어두운 배경. **저장으로 닫아야
 * 반영되고**, 나머지 둘로 닫으면 적던 것이 남지 않습니다. 그래서 여기서
 * 적는 동안은 초안을 들고 있다가 저장할 때 한 번에 넘깁니다 — 필터 시트
 * (C1-4)와 같은 규칙입니다.
 *
 * **결단도 같은 시트로 적습니다.** 다만 내용 한 칸이고 대표 공유가 없습니다 —
 * 결단은 남의 기도제목을 가져오는 일이 아니기 때문입니다.
 */

interface Props {
  entry: Entry;
  kind: ItemKind;
  /** 시트 오른쪽 위에 적는 기록지 제목 */
  sheetTitle: string;
  /** 내 줄인가 — 머리글과 Prayer Book 버튼이 갈립니다 */
  mine: boolean;
  onClose: () => void;
  onSaved: (sheet: Sheet) => void;
  /** F4-2-1 로 넘어갑니다 — 영역 · PLAN 을 고르는 화면이 따로 뜹니다 */
  onSaveToBook: () => void;
}

export function EntryWriter({
  entry,
  kind,
  sheetTitle,
  mine,
  onClose,
  onSaved,
  onSaveToBook,
}: Props) {
  const shareable = kind === 'PRAYER_NOTE';

  const [mode, setMode] = useState<EntryMode>(entry.mode);
  const [title, setTitle] = useState(entry.title);
  const [body, setBody] = useState(entry.body);
  const [chosen, setChosen] = useState<string[]>(entry.notes.map((n) => n.id));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /* 고를 대표 기도제목은 열고 나서 한 번만 읽습니다 */
  const [options, setOptions] = useState<PrayerNote[] | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (mode !== 'SHARED' || options !== null || !shareable) return;
    let alive = true;
    setLoadingOptions(true);
    shareableNotes(entry.entryId)
      .then((notes) => {
        if (alive) setOptions(notes);
      })
      .catch((e: unknown) => {
        if (alive) setFailure(e instanceof ApiError ? e.message : '대표 기도제목을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (alive) setLoadingOptions(false);
      });
    return () => {
      alive = false;
    };
  }, [mode, options, shareable, entry.entryId]);

  const toggle = (id: string) =>
    setChosen((was) => (was.includes(id) ? was.filter((x) => x !== id) : [...was, id]));

  const canSave =
    mode === 'WRITTEN' ? body.trim() !== '' : chosen.length > 0;

  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      onSaved(
        await patchEntry(
          entry.entryId,
          mode === 'WRITTEN'
            ? { mode, title: title.trim(), body: body.trim() }
            : { mode, noteIds: chosen },
        ),
      );
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
      setBusy(false);
    }
  };

  const name = entry.member.name ?? '이름 없음';
  const linked = entry.mode === 'WRITTEN' && entry.notes.length > 0;

  return (
    <BottomSheet onClose={onClose}>
      <div className="writer__head">
        <span className="writer__who">
          <Avatar name={name} seed={entry.member.userId} />
          <b>{mine ? `내 ${kind === 'DECISION' ? '결단' : '기도 제목'}` : `${name}의 ${kind === 'DECISION' ? '결단' : '기도 제목'}`}</b>
        </span>
        {/* 대신 적어 준 줄이면 그것을 먼저 말합니다 (F4-2-1) */}
        {entry.writtenBy ? (
          <span className="writer__by">그룹장 {entry.writtenBy.name ?? ''}이 적음</span>
        ) : (
          <span className="writer__sheet">{sheetTitle}</span>
        )}
      </div>

      <div className="sheet__scroll">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {/* 결단에는 대표 공유가 없습니다 — 고를 것이 없으므로 탭도 두지 않습니다 */}
        {shareable && (
          <Seg
            label="적는 방식"
            options={[
              { value: 'WRITTEN', label: '직접 적기' },
              { value: 'SHARED', label: '대표 기도제목 공유' },
            ]}
            value={mode}
            onChange={setMode}
          />
        )}

        {mode === 'WRITTEN' ? (
          <>
            {kind === 'PRAYER_NOTE' && (
              <label className="field">
                <span className="field__label">제목 (선택)</span>
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
            )}

            <label className="field">
              <span className="field__label">내용 (필수)</span>
              <textarea
                className="input input--tall"
                value={body}
                autoFocus
                onChange={(e) => setBody(e.target.value)}
              />
            </label>

            <p className="hint">
              {mine
                ? '제목 없이 저장하면 목록에서 내용이 제목 자리에 옵니다.'
                : `${name}도 자기 줄에서 고칠 수 있습니다.`}
            </p>

            {/*
              F4-2-1 — 내 줄에서만. 이미 담았으면 다시 담을 수 없습니다.
              담긴 뒤에는 Prayer Book 에서 고친 것이 이 줄에도 그대로 옵니다.
            */}
            {mine && kind === 'PRAYER_NOTE' && (
              <button
                type="button"
                className="mrow"
                disabled={linked || entry.body.trim() === ''}
                onClick={onSaveToBook}
              >
                <Icon name="book" size={20} />
                <span className="mrow__grow">
                  {linked ? '내 Prayer Book 에 담겨 있습니다' : '내 Prayer Book 으로 저장'}
                  <span className="mrow__sub">
                    {linked
                      ? 'Prayer Book 에서 고치면 이 줄도 함께 바뀝니다'
                      : '영역과 PLAN 을 골라 내 기도 노트로 담습니다'}
                  </span>
                </span>
              </button>
            )}
          </>
        ) : (
          <>
            {loadingOptions && <Loading label="대표 기도제목을 불러오는 중" />}

            {options !== null && options.length === 0 && (
              <div className="empty">
                <p className="empty__title">
                  {mine ? '아직 대표 기도제목이 없습니다' : `${name}의 대표 기도제목이 없습니다`}
                </p>
                <p className="empty__hint">
                  {mine
                    ? 'Prayer Book 의 노트 설정에서 대표로 지정하면 여기서 고를 수 있습니다.'
                    : '직접 적기로 받아 적어 주세요.'}
                </p>
              </div>
            )}

            {/* 여러 개를 함께 고를 수 있습니다 */}
            <div className="sharelist">
              {(options ?? []).map((n) => {
                const on = chosen.includes(n.id);
                return (
                  <button
                    key={n.id}
                    type="button"
                    className={on ? 'sharepick sharepick--on' : 'sharepick'}
                    aria-pressed={on}
                    onClick={() => toggle(n.id)}
                  >
                    <span className="sharepick__grow">
                      <span className="card__pills">
                        <StatusPill status={n.status} />
                        {n.priority !== 'NORMAL' && <PriorityPill priority={n.priority} />}
                      </span>
                      <span className="sharepick__title">{n.title}</span>
                      {n.body !== '' && <span className="sharepick__body">{n.body}</span>}
                    </span>
                    <span className={on ? 'chk chk--on' : 'chk'}>
                      {on && <Icon name="check" size={12} />}
                    </span>
                  </button>
                );
              })}
            </div>

            {(options?.length ?? 0) > 0 && (
              <p className="hint">
                고른 것이 이 줄에 한 줄씩 보입니다. <b>원본을 고치면 여기도 함께 바뀝니다.</b>
              </p>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        className="btn btn--primary"
        disabled={busy || !canSave}
        onClick={save}
      >
        {busy ? '저장 중…' : '저장'}
      </button>
    </BottomSheet>
  );
}
